import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { api } from "@webcore/services/api/api";
import { containerToTags } from "@webcore/services/signalr/containerTagMapping";
import { bumpDataChangeVersions } from "@webcore/services/signalr/dataChangeVersions";
import { getClientInstanceId } from "@webcore/services/clientInstanceId";
import type { DataChangedHandler } from "@webcore/services/signalr/SignalRService";

const DEBOUNCE_MS = 1500;

// Tags whose RTK providers carry a per-configuration parent id (see providesTags on getKpiData /
// getDashboardRecords in api.ts), so a scoped notification can invalidate one pipeline
// configuration's entries instead of the whole tag type. A scope on any other tag is ignored — its
// providers use unrelated id schemes, and a scoped invalidation there would match nothing.
const SCOPE_CAPABLE_TAGS: ReadonlySet<string> = new Set(["KpiData", "DashboardRecords"]);

/**
 * Returns a handler that maps a data-change notification to the matching RTK Query cache tags and
 * invalidates them, debounced. The server's explicit tags + configuration scope are preferred (see
 * DataChangeNotificationPolicy on the backend); notifications without them — older servers, or
 * changes with no finer classification — fall back to the static container → tag mapping. Echoes of
 * this client's own writes are ignored via the originating instance id.
 */
export function useDataChangeInvalidator(): DataChangedHandler {
  const dispatch = useDispatch();
  const debounceTimerRef = useRef<number | null>(null);
  const pendingTagsRef = useRef<Set<string>>(new Set());
  const pendingScopedRef = useRef<Map<string, { tag: string; scope: string }>>(new Map());

  useEffect(() => {
    return () => {
      pendingTagsRef.current.clear();
      pendingScopedRef.current.clear();
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  return useCallback((containerName, sourceClientInstanceId, tags, scope) => {
    if (sourceClientInstanceId === getClientInstanceId()) return;

    const effectiveTags = tags ?? containerToTags[containerName];
    if (effectiveTags) {
      for (const tag of effectiveTags) {
        if (scope && SCOPE_CAPABLE_TAGS.has(tag)) {
          pendingScopedRef.current.set(`${tag}::${scope}`, { tag, scope });
        } else {
          pendingTagsRef.current.add(tag);
        }
      }
    }

    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = window.setTimeout(() => {
      const bareTags = [...pendingTagsRef.current];
      const scoped = [...pendingScopedRef.current.values()];
      pendingTagsRef.current.clear();
      pendingScopedRef.current.clear();
      debounceTimerRef.current = null;

      const tagDescriptors = [
        ...bareTags,
        ...scoped.map(({ tag, scope: tagScope }) => ({ type: tag, id: tagScope })),
      ];
      dispatch(api.util.invalidateTags(tagDescriptors as Parameters<typeof api.util.invalidateTags>[0]));

      // Consumers that fetch outside the query cache (custom dashboard view code) refresh off these
      // counters rather than off the tag invalidation.
      bumpDataChangeVersions(bareTags);
      scoped.forEach(({ tag, scope: tagScope }) => bumpDataChangeVersions([tag], tagScope));
    }, DEBOUNCE_MS);
  }, [dispatch]);
}
